import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parse } from "date-fns";
import moment from "moment";

import { createAxiosInstance } from "api/axiosConfig";
import { getServerErrorMessage } from "utils/showMutationError";

const getReferralData = ({
  fileAttachments,
  preferredConsultantFamilyName,
  preferredConsultantFirstName,
  mothersMaidenName,
  email,
  phone,
  mobile,
  streetAddress,
  addressLine2,
  addressLine3,
  addressLine4,
  interpreterRequired,
  currentMedication,
  wheelchairAssistanceRequired,
  eirCode,
  gender,
  dateOfBirth,
  familyName,
  firstName,
  title,
  medications,
  hospitalName,
  hospitalId,
  departmentAgencyCode,
  departmentAgencyName,
  drinker,
  smoker,
  referralPriority,
  referralType,
  relevantFamilyHistory,
  patientOnAnticoagulants,
  unitsOfAlcohol,
  yearsSmoking,
  cigarettesSmokedPerDay,
  nextOfKin,
  additionalInformation,
  bmi,
  bodyHeight,
  weight,
  bp1,
  bp2,
  pulse,
  reasonForReferral,
  historyOfPresentingComplains,
  previousHospitalAttendance,
  historyOfPastIllness,
  historyOfSurgicalProcedures,
  allergiesAdverseMedicationEvents,
  laboratoryInvestigation,
  radiologyInvestigation,
  firstLanguage,
  patientId,
  gpOutofHoursNumber,
  clinicalExamination,
  gpPhone,
  gpAddress,
  gpCity,
  gpPostalCode,
}) => {
  return {
    outOfHoursNumber: gpOutofHoursNumber,
    fileAttachments: fileAttachments,
    receivingFacility: {
      hospitalName: hospitalName,
      hospitalId: hospitalId,
      agencyId: departmentAgencyCode,
      departmentAgencyName: departmentAgencyName,
    },
    referralInformation: {
      status: "PENDING",
      priority: referralPriority,
      referralType: referralType,
      referralDate: format(new Date(), "yyyy-MM-dd"),
    },
    providers: [
      {
        firstName: "",
        lastName: "",
        role: "PRIMARY_CARE_PROVIDER",
        outOfHoursNumber: gpOutofHoursNumber,
        referralAddress: {
          addressLine1: gpAddress || "",
          city: gpCity || "",
          postalCode: gpPostalCode || "",
        },
        communicationInfo: [
          {
            phoneNumber: gpPhone || "",
            telecommunicationUseCode: "WORK_NUMBER",
          },
        ],
      },
      {
        role: "REFERRED_TO_PROVIDER",
        firstName: preferredConsultantFirstName,
        lastName: preferredConsultantFamilyName,
      },
    ],
    patientIdentification: {
      firstName: firstName,
      lastName: familyName,
      mothersMaidenName: mothersMaidenName,
      prefix: title,
      gender: gender,
      identifiers: [
        {
          identifier: "",
          identifierType: "MEDICAL_RECORD_NUMBER",
          issuingAuthority: {
            id: "HEALTHLINK",
            checkDigit: "",
            codeIdentifyingCheckDigit: "",
          },
        },
      ],
      referralAddress: {
        addressLine1: streetAddress,
        addressLine2: addressLine2,
        addressLine3: addressLine3,
        city: addressLine4,
        postalCode: eirCode,
      },
      dateOfBirth: dateOfBirth,
      communicationInfo: [
        {
          phoneNumber: phone,
          telecommmunicationEquipmentType: "TELEPHONE",
          telecommunicationUseCode: "PRIMARY_RESIDENCE_NUMBER",
          emailAddress: "",
        },
        ...(email
          ? [
              {
                phoneNumber: "",
                telecommmunicationEquipmentType: "INTERNET",
                telecommunicationUseCode: "NETWORK_EMAIL_ADDRESS",
                emailAddress: email,
              },
            ]
          : []),
        ...(mobile
          ? [
              {
                phoneNumber: mobile,
                telecommmunicationEquipmentType: "CELLULAR_PHONE",
                telecommunicationUseCode: "ANSWERING_SERVICE_NUMBER",
                emailAddress: "",
              },
            ]
          : []),
      ],
      firstLanguage: firstLanguage,
    },
    historyGeneral: {
      reasonForReferral: reasonForReferral,
      previousHospitalAttendance: previousHospitalAttendance,
      historyOfPresentingIllness: historyOfPresentingComplains,
      historyOfPastIllness: historyOfPastIllness,
      historyOfSurgicalProcedures: historyOfSurgicalProcedures,
      historyOfAllergies: allergiesAdverseMedicationEvents,
      historyOfFamilialIllness: relevantFamilyHistory,
      additionalRelevantInformation: additionalInformation,
    },
    socialHistory: {
      alcoholPerWeek: unitsOfAlcohol,
      historyOfAlcoholUse: drinker,
      nextOfKin: nextOfKin,

      historyOfTobaccoUse: smoker,
      numberOfCigarettesPerDay: cigarettesSmokedPerDay,
      numberOfYearsSmoking: yearsSmoking,

      interpreterRequired: interpreterRequired,
      wheelchairAssistanceRequired: wheelchairAssistanceRequired,
    },
    clinicalExamination: {
      examination: clinicalExamination,
      systolicBloodPressureMMHg: bp1,
      diastolicBloodPressureMMHg: bp2,
      heartRatePerMinute: pulse,
      bodyHeightMeter: bodyHeight,
      bodyWeightKg: weight,
      bodyMassIndexKgPerM2: bmi,
    },
    laboratoryStudies: laboratoryInvestigation
      .filter((lab) => lab.title)
      .map((lab) => ({
        laboratoryInvestigation: lab.title,
        dateOfInvestigation: lab.date
          ? format(parse(lab.date, "dd-MM-yyyy", new Date()), "yyyy-MM-dd")
          : "",
        result: lab.result,
        unit: lab.unit ?? "",
      })),
    radiologyStudyReports: radiologyInvestigation
      .filter((rad) => rad.title)
      .map((rad) => ({
        radiologyInvestigation: rad.title,
        dateOfInvestigation: rad.date
          ? format(parse(rad.date, "dd-MM-yyyy", new Date()), "yyyy-MM-dd")
          : "",
        result: rad.result,
        hospital: rad.hospital ?? "",
      })),
    currentMedications: {
      anticoagulantUse: patientOnAnticoagulants,
      currentMedication: currentMedication,
      referralMedications:
        patientOnAnticoagulants === "true"
          ? medications.map((med) => ({
              medicationName: med.name,
              medicationDosage: med.dosage,
            }))
          : [],
    },
    patientVisit: {
      patientClass: "OUTPATIENT",
    },
    patientId: patientId,
  };
};

const SEND_REFERRAL_FALLBACK =
  "Something unexpected happened while saving the referral, please try again";

/*
 * The backend's own sentence (KIN-1495). `error.message` alone was axios transport text
 * — "Request failed with status code 400" — whenever the envelope's message wasn't
 * lifted onto it, and a message-building refusal (a gender Healthlink can't carry) now
 * arrives as a 400 saying exactly that.
 *
 * A refusal raised here rather than by the server (a 200 that carried no link, no
 * success) has no response body to read, so its own message is the message.
 */
const toSendReferralMessage = (error) =>
  error?.response
    ? getServerErrorMessage(error, SEND_REFERRAL_FALLBACK)
    : error?.message || SEND_REFERRAL_FALLBACK;

export const useSendReferral = ({ onSuccess }) => {
  const queryClient = useQueryClient();
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (data) => {
      const body = getReferralData({
        ...data,
        patientId: data.patientId,
        today: moment().format("DD-MM-YYYY"),
        isSpecialist: undefined,
      });

      const axiosInstance = createAxiosInstance();

      if (data?.isSpecialist) {
        const response = await axiosInstance.post(
          `${tenantVal}/healthlink/referrals/specialist`,
          {
            generalReferral: body,
            specialistReferralType: data.referralType,
            service: data.service,
          }
        );

        if (!response.data?.link) {
          throw new Error(
            response?.data?.errorMessage ||
              "Healthlink returned no link for this specialist referral."
          );
        }

        return response.data;
      }

      const response = await axiosInstance.post(
        `${tenantVal}/healthlink/referrals`,
        body
      );

      if (!response.data?.success) {
        throw new Error(
          response?.data?.errorMessage ||
            "Healthlink did not confirm this referral was sent."
        );
      }

      return response.data;
    },
    onSuccess: (data) => {
      toast.success("Referral saved successfully!");
      queryClient.invalidateQueries({ queryKey: ["referralsList"] });
      queryClient.invalidateQueries({ queryKey: ["referralXml"] });
      queryClient.invalidateQueries({ queryKey: ["activeConsultationReferral"] });
      queryClient.invalidateQueries({ queryKey: ["activeConsultation"] });
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error(error);
      toast.error(toSendReferralMessage(error));
    },
  });
};
